import React, { useState, useEffect, useDebugValue } from 'react';
import Select, { components } from "react-select";
import { useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Sidebar from './Sidebar';
import { Session } from "./utils/session";
import { API_BASE_URL } from '../config';
import { useNavigate } from "react-router-dom";

const Option = (props) => {
  return (
    <components.Option {...props}>
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        style={{
          margin: 0,
          marginRight: "10px" // gap between checkbox and label
        }}
      />
      <label style={{ margin: 0}}>
        {props.label}
      </label>
    </components.Option>
  );
};
export const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

const KanbanCard = React.memo(({ task, index, onCardClick, isDragDisabled }) => {
  return (
    <Draggable draggableId={task.id} index={index} isDragDisabled={isDragDisabled}>
      {(provided, snapshot) => (
        <div
          className={`kanban-card p-3 rounded-lg shadow-sm border mb-2 
            ${isDragDisabled ? 'bg-light text-muted border-success' : 'bg-white'} 
            ${snapshot.isDragging ? 'shadow-lg' : ''}`}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          onClick={() => onCardClick(task)}
          style={{ ...provided.draggableProps.style, opacity: isDragDisabled ? 0.8 : 1 }}
        >
          {task.image && (
            <img src={task.image} alt="Task attachment" className="card-img-top mb-2 rounded" />
          )}
          <p className="mb-2" style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>{task.content} - {task.doctorName}

          </p>
          {!isDragDisabled && (
            <div className="d-flex justify-content-between align-items-start">

              <div className="w-100">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className="badge bg-primary-subtle text-primary-emphasis">
                    {task.userType}
                  </span>

                  <span
                    className="badge bg-success-subtle text-success-emphasis"
                    style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                  { !task.assignedUser ||task.assignedUser === "0" || task.assignedUser === 0? "Unassigned" : task.assignedUser }
                  </span>

                  <img
                    src="/Images/user.png"
                    height="20"
                    width="20"
                    alt="User avatar"
                    className="rounded-circle"
                  />
                </div>

                <div className="d-flex align-items-start gap-3 mt-4">

                  {/* Due Date – left side */}
                  {task.dueDate && (
                    <small className="text-muted fw-bold" style={{ whiteSpace: "nowrap" }}>
                      {task.dueDate}
                    </small>
                  )}

                  {/* Priority + Rush Case (vertical) */}
                  <div className="d-flex align-items-center gap-2">

                    <span
                      className="badge bg-warning-subtle text-warning-emphasis"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {task.prioritys ? `Priority : ${task.prioritys}` : "No Priority"}
                    </span>

                    {Number(task.prioritys) === 1 && (
                      <span
                        className="badge bg-danger-subtle text-danger-emphasis"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Rush Case
                      </span>
                    )}

                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      )}
    </Draggable>
  );
});

const KanbanColumn = React.memo(({ column, tasks, onCardClick, openModalSheet }) => {

  const role = Session.getRole();
  const adminRoles = ["Super Admin", "Admin", "Team Leader"];
  const isMember = !adminRoles.includes(role);

  return (
    <div className="kanban-column-wrapper d-flex flex-column h-100">
      <div className="bg-light p-3 rounded-3 shadow-sm border-top border-primary border-3 flex-grow-1">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <h3 className="h6 fw-bold mb-0 me-2">{column.title}</h3>
            <span className="badge bg-dark-subtle text-dark-emphasis rounded-pill">{tasks.length}</span>
          </div>
          <div className="kanban-column-controls">
            <button className="btn btn-sm btn-light">
              <i className="fas fa-ellipsis-h" />
            </button>
          </div>
        </div>

        <StrictModeDroppable droppableId={column.id} type="task">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="kanban-card-list mt-3"
            >
              {tasks.map((task, index) => <KanbanCard key={task.id} task={task} index={index} onCardClick={onCardClick} isDragDisabled={false} />)}
              {provided.placeholder}
            </div>
          )}
        </StrictModeDroppable>

        {!isMember && (
          <button
            className="kanban-add-card btn btn-outline-secondary w-100 border-dashed py-2 d-flex align-items-center justify-content-center mt-2"
            onClick={openModalSheet}
          >
            <i className="fas fa-plus me-2" />
            <span>Add a card</span>
          </button>
        )
        }
      </div>
    </div>
  );
});

export default function DailyTask() {

  const role = Session.getRole();
  const adminRoles = ["Super Admin", "Admin", "Team Leader"];
  const isMember = !adminRoles.includes(role);

  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);
  const [boardData, setBoardData] = useState({ tasks: {}, columns: {}, columnOrder: [] });
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [selectedWorkspaceName, setSelectedWorkspaceName] = useState("");
  const [selectedSheetName, setSelectedSheetName] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 200;
  const [totalRecords, setTotalRecords] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [saving, setSaving] = useState(false);

  const totalPages = Math.ceil(totalRecords / pageSize) || 1;

  const start = (pageNumber - 1) * pageSize + 1;  // first record number
  const end = Math.min(pageNumber * pageSize, totalRecords); // last record number

  const startPage = Math.floor((pageNumber - 1) / 5) * 5 + 1; // first page button
  const endPage = Math.min(startPage + 4, totalPages);  // last page button
  const loginUserId = Session.getUserId();

  const navigate = useNavigate();

  const handleSheetChange = (e) => {
    const id = e.target.value;
    setBoardData({
      tasks: {},
      columns: {},
      columnOrder: []
    });
    setSelectedWorkspaceId(id);

    const ws = workspaces.find(
      w => String(w.workspaceID) === id
    );

    if (!ws) {
      setSelectedWorkspaceName("");
      setSelectedSheetName("");
      return;
    }

    setSelectedWorkspaceName(ws.workspaceName); // IME
    setSelectedSheetName(ws.description);       // IME Sheet 2026

    localStorage.setItem("selectedSheetId", id);
    localStorage.setItem("selectedSheetName", ws.description);
  };

  useEffect(() => {
    const loadWorkspaces = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/Workspace/Workspaces_List`,
          {
            headers: {
              Authorization: `Bearer ${Session.getToken()}`,
              "Content-Type": "application/json"
            }
          }
        );

        const data = await res.json();
        setWorkspaces(Array.isArray(data) ? data : []);

        const ime = data.find(w => w.workspaceName === "IME");

        const savedId = localStorage.getItem("selectedSheetId");

        if (savedId) {
          setSelectedWorkspaceId(savedId);

          const ws = data.find(w => String(w.workspaceID) === savedId);

          if (ws) {
            setSelectedWorkspaceName(ws.workspaceName); // IME
            setSelectedSheetName(ws.description);       // IME Sheet 2026
          }
        }
        else if (ime) {
          setSelectedWorkspaceId(String(ime.workspaceID));
          setSelectedWorkspaceName(ime.workspaceName);
          setSelectedSheetName(ime.description);
        }

      } catch (err) {
        console.error("Workspace load error:", err);
      }
    };

    loadWorkspaces();
  }, []);
  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.style.display = showMobileSidebar ? 'block' : 'none';
    }
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      if (showMobileSidebar) sidebar.classList.add('show-mobile');
      else sidebar.classList.remove('show-mobile');
      if (hideDesktop) sidebar.classList.add('hide-desktop');
      else sidebar.classList.remove('hide-desktop');
    }
    const content = document.getElementById('content');
    if (content) {
      if (hideDesktop) content.classList.add('full-width');
      else content.classList.remove('full-width');
    }
  }, [showMobileSidebar, hideDesktop]);

  const toggleSidebar = () => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar((s) => !s);
    } else {
      setHideDesktop((s) => !s);
    }
  };

  const loadCardsBySheet = useCallback(async () => {
    try {
      setLoading(true);
      if (!selectedWorkspaceName) {
        setLoading(false);
        return;
      }

      let apiUrl = "";

      if (selectedWorkspaceName === "IME") {
        let query = new URLSearchParams();

        query.append("pageNumber", pageNumber);
        query.append("pageSize", pageSize);
        query.append("roleName", role);
        query.append("loginUserID", loginUserId);
        console.log("loginUserID:", loginUserId);
        console.log("roleName", role);
        if (taskSearch) {
          query.append("searchText", taskSearch);
        }

        if (selectedUserId === "unassigned") {
          query.append("unassigned", 1);
        } else if (selectedUserId) {
          query.append("assignedUserId", selectedUserId);
        }

        if (fromDate) {
          query.append("fromDate", fromDate);
        }

        if (toDate) {
          query.append("toDate", toDate);
        }
        apiUrl = query.toString().length > 0
          ? `/api/Cards/CardsList-IME?${query.toString()}`
          : "/api/Cards/CardsList-IME";
      }
      else if (selectedWorkspaceName === "PHADS") {
        apiUrl = "/api/Cards/CardsList-PHAD";
      }
      else if (selectedWorkspaceName === "PEERS") {
        apiUrl = "/api/Cards/CardsList-PEER";
      }
      else {
        console.warn("No API mapped for:", selectedWorkspaceName);
        return;
      }

      const [statusRes, cardsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/CardStatus/list`, {
          headers: {
            Authorization: `Bearer ${Session.getToken()}`,
            "Content-Type": "application/json"
          }
        }),
        fetch(`${API_BASE_URL}${apiUrl}`, {
          headers: {
            Authorization: `Bearer ${Session.getToken()}`,
            "Content-Type": "application/json"
          }
        })
      ]);

      if (!statusRes.ok || !cardsRes.ok) {
        console.error("API Error");
        return;
      }

      const statuses = await statusRes.json();
      const response = await cardsRes.json();

      const cardsList = response.data || [];
      setTotalRecords(response.totalRecords || 0);
      const columns = {};
      const columnOrder = [];
      const tasks = {};

      statuses.forEach(status => {
        const colId = status.cardStatusID.toString();
        columns[colId] = { id: colId, title: status.statusName, taskIds: [] };
        columnOrder.push(colId);
      });

      cardsList.forEach(item => {
        let cardId = "";
        let taskId = "";

        if (selectedWorkspaceName === "IME") {
          cardId = item.imeSheetID;
          taskId = `IME-${item.imeSheetID}-${item.userType}-${item.assignedUserId}`;
        } else {
          cardId = item.imeID || item.phadid || item.peersid || item.claimant;
          taskId = `card-${cardId}`;
        }

        tasks[taskId] = {
          id: taskId,
          content: item.docID || item.caseID || item.claimant,
          description: item.description || "",
          image: null,
          userType: item.userType,
            assignedUserId: item.assignedUserId,

  assignedUserIds: item.allAssignedUserIds
    ? String(item.allAssignedUserIds).split(",")
    : [],

  allAssignedUserIds: item.allAssignedUserIds,

  assignedUser: item.assignedUser,

  allAssignedUsers: item.allAssignedUsers,
          doctorName: item.doctorName,
          vendor: item.vendor,
          prioritys: item.prioritys,
          dueDate: item.date ? item.date.split("T")[0] : "",
          claimantPath: item.claimantPath
        };

        const statusId = (item.cardStatusID || 1).toString();
        if (columns[statusId]) {
          columns[statusId].taskIds.push(taskId);
        }
      });

      setBoardData({ tasks, columns, columnOrder });
    } catch (err) {
      console.error("Board Load Error:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedWorkspaceName, taskSearch, selectedUserId, pageNumber, pageSize, fromDate, toDate]);


  useEffect(() => {
    fetch(`${API_BASE_URL}/api/Members/Users_List`, {
      headers: {
        Authorization: `Bearer ${Session.getToken()}`,
        "Content-Type": "application/json"
      }
    })
      .then(res => res.json())
      .then(data => setUsers(data || []))
      .catch(err => console.error(err));
  }, []);


  useEffect(() => {
    if (selectedWorkspaceName) {
      loadCardsBySheet();
    }
  }, [taskSearch, selectedUserId, selectedWorkspaceName, pageNumber, fromDate, toDate, loadCardsBySheet]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) {
        setShowMobileSidebar(false);
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (selectedTask) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'auto';
    };
  }, [selectedTask]);

  const handleCardClick = (task) => {
    const latestTask = boardData.tasks[task.id];
    setSelectedTask({
      ...latestTask,
      userType: latestTask.userType || "",
    
    });
  };

  const handleCloseModal = async () => {
    setSelectedTask(null);
  };


  const handleSaveClick = async () => {
    debugger;
    if (!selectedTask) return;

    setSaving(true);

    const loginUserId = Session.getUserId();

    try {

      let apiUrl = "";
      let payload = {};

      if (selectedWorkspaceName === "IME") {

        const parts = selectedTask.id.split("-");
        const imeId = parts[1]; // IMESheetID

        if (!imeId || !selectedTask.userType) {
          alert("Invalid IME data ");
          return;
        }

        apiUrl = "/api/Cards/Ime-card-update";

        payload = {
          IMESheetID: Number(imeId),
          UserType: selectedTask.userType,
          Description: selectedTask.description || "",
          AssignedUserID:
             selectedTask.assignedUserIds?.length > 0
               ? selectedTask.assignedUserIds.join(",")
               : null,
          Prioritys:
            selectedTask.prioritys
              ? Number(selectedTask.prioritys)
              : null
        };
      }

      else {

        const cardId = Number(selectedTask.id.replace("card-", ""));

        if (!cardId) {
          alert("Invalid Card ID ❌");
          return;
        }

        apiUrl = "/api/Cards/card-update";

        payload = {
          sheetName: selectedSheetName,
          cardID: cardId,
          Description: selectedTask.description || "",

          assignedUserID:
            selectedTask.assignedUserId
              ? Number(selectedTask.assignedUserId)
              : null,

          Priority: selectedTask.prioritys
            ? Number(selectedTask.prioritys)
            : null,

          dueDate: selectedTask.dueDate
            ? new Date(selectedTask.dueDate).toISOString()
            : null,

          CreatedUserID: loginUserId
        };
      }
      const res = await fetch(`${API_BASE_URL}${apiUrl}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Session.getToken()}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setSaving(false);
        alert(data.message || "Update failed. Please try again.");
        return;
      }
      alert(data.message || "Updated Successfully.");
      setSelectedTask(null);
      await loadCardsBySheet();
      setSaving(false);

    } catch (err) {
      console.error("Save Error:", err);
      setSaving(false);
      alert("Update failed ❌");
    }
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    // const cardId = Number(taskToDelete.replace("task-", ""));
    const parts = taskToDelete.split("-");
    const imesheetid = Number(parts[1]);
    try {
      const res = await fetch(`${API_BASE_URL}/api/Cards/${imesheetid}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Session.getToken()}`
        }
      });

      if (!res.ok) throw new Error("Delete failed");

      // 🔹 Refresh board from DB
      await loadCardsBySheet();
      setSelectedTask(null);
      setShowDeleteConfirm(false);
      setTaskToDelete(null);

    } catch (err) {
      console.error(err);
      alert("Failed to delete card");
    }
  };


  const handleDeleteClick = (taskId) => {
    setTaskToDelete(taskId);
    setShowDeleteConfirm(true);
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setTaskToDelete(null);
  };

  const onDragEnd = async (result) => {

    const { source, destination, draggableId } = result;

    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) return;

    const prevBoard = JSON.parse(JSON.stringify(boardData));

    setBoardData(prev => {

      const start = prev.columns[source.droppableId];
      const finish = prev.columns[destination.droppableId];

      const startIds = Array.from(start.taskIds);
      startIds.splice(source.index, 1);

      const finishIds = Array.from(finish.taskIds);
      finishIds.splice(destination.index, 0, draggableId);

      return {
        ...prev,
        columns: {
          ...prev.columns,
          [start.id]: { ...start, taskIds: startIds },
          [finish.id]: { ...finish, taskIds: finishIds }
        }
      };
    });

    let apiUrl = "";
    let payload = {};
    let userType = null;

    const newStatusId = Number(destination.droppableId);

    if (selectedWorkspaceName === "IME") {

      const parts = draggableId.split("-");

      const imeId = parts[1];
      userType = parts[2];

      apiUrl = "/api/Cards/Ime-update-status";

      payload = {
        IMESheetID: Number(imeId),
        NewStatusId: newStatusId,
        UserType: userType,
        ClaimantPath: boardData.tasks[draggableId]?.claimantPath,
        IsTracking: false,
        DoneUserId: boardData.tasks[draggableId]?.assignedUserId
      };

    }
    else {

      const cardId = draggableId.replace("card-", "");

      apiUrl = "/api/Cards/update-status";

      payload = {
        cardID: Number(cardId),
        cardStatusID: newStatusId
      };
    }
    try {

      const res = await fetch(`${API_BASE_URL}${apiUrl}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Session.getToken()}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error("Server Error");
      }

      const data = await res.json();

      // Add this 
      const doneColumn = Object.values(boardData.columns).find(
        col => col.title.toLowerCase() === "done"
      );

      const doneTaskIds = doneColumn ? doneColumn.taskIds : [];

      if (userType === "QC") {
        const allTasks = Object.values(boardData.tasks);

        const sameGroupTasks = allTasks.filter(t =>
          t.content === boardData.tasks[draggableId]?.content
        );

        console.log("draggableId:", draggableId);

        const isRORCompleted = sameGroupTasks.some(
          t => t.userType === "ROR" &&
            doneTaskIds.includes(t.id)
        );

        const isTemplateCompleted = sameGroupTasks.some(
          t => t.userType === "Template" &&
            doneTaskIds.includes(t.id)
        );

        if (!isRORCompleted || !isTemplateCompleted) {
          alert("You can not move this task from block, please complete Template task first.");
          setBoardData(prevBoard);
          return;
        }
      }

      if (data.success !== true && userType !== "ROR" && userType !== "Template" && userType !== "QC") {

        let errorMsg = 'Next process already started, so cannot move back to inprocess';
        if (selectedWorkspaceName === "IME") {

          if (userType === "Extraction" && data.message) {
            errorMsg = data.message;
          }
          else {
            switch (userType) {

              case "ROR":
                errorMsg = "You can not move this task from block, please complete Extraction task first.";
                break;

              case "Template":
                errorMsg = "You can not move this task from block, please complete Extraction task first.";
                break;

              // case "QC":
              //   errorMsg = "You can not move this task from block, please complete Template task first.";
              //   break;
              default:
                errorMsg = "Next process already started, so cannot move back to inprocess";
            }
          }
        }
        alert(errorMsg);
        setBoardData(prevBoard);

        return;
      }

    } catch (err) {

      console.error("Update Failed:", err);
      alert("Something went wrong");
      setBoardData(prevBoard);
    }

  };

  const openAddModal = () => {

    if (!selectedWorkspaceName) {
      alert("Please select sheet first!");
      return;
    }

    if (selectedWorkspaceName === "IME") {
      navigate("/Imesheet", {
        state: {
          sheetName: selectedSheetName,
          workspaceId: selectedWorkspaceId,
          fromAddCard: true
        }
      });
      return;
    }

    if (selectedWorkspaceName === "PEERS") {
      navigate("/Peersheet", {
        state: {
          sheetName: selectedSheetName,
          workspaceId: selectedWorkspaceId,
          fromAddCard: true
        }
      });
      return;
    }

    // PHAD
    if (selectedWorkspaceName === "PHADS") {
      navigate("/Phadsheet", {
        state: {
          sheetName: selectedSheetName,
          workspaceId: selectedWorkspaceId,
          fromAddCard: true
        }
      });
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      loadCardsBySheet();
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, [loadCardsBySheet]);
  return (
    <div>
      {/* Overlay for mobile sidebar - always rendered, visibility controlled by useEffect */}
      <div id="sidebar-overlay" onClick={() => setShowMobileSidebar(false)} />
      <div id="sidebar">
        <Sidebar />
      </div>
      <div id="content">
        <div className="d-flex align-items-center mb-3">
          <button type="button" id="sidebarCollapse" className="btn btn-toggle me-3" onClick={toggleSidebar}>
            <i className="fas fa-bars" />
          </button>
          <a href="/" className="text-muted text-decoration-none small"><i className="fas fa-chevron-left" /> Back</a>
        </div>
        <div className="d-flex justify-content-between align-items-center mb-4 page-header flex-wrap gap-2">
          <h2>Tasks</h2>
          <div className="d-flex align-items-center gap-2">
            <label className="fw-semibold mb-0">Teams:</label>
            <select
              value={selectedWorkspaceId}
              onChange={handleSheetChange}
            >
              {workspaces.map((w) => (
                <option key={w.workspaceID} value={w.workspaceID}>
                  {w.workspaceName}
                </option>

              ))}
            </select>
          </div>


        </div>
        <div className="row g-2 mb-3">
          {!isMember && (
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={selectedUserId}
                onChange={(e) => {
                  setSelectedUserId(e.target.value);
                  setPageNumber(1);
                }}
              >
                <option value="">All Users</option>
                <option value="unassigned">Unassigned</option>
                {users.map(u => (
                  <option key={u.userId} value={u.userId}>
                    {u.userName}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="col-md-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search task"
              value={taskSearch}
              onChange={(e) => {
                setTaskSearch(e.target.value);
                setPageNumber(1);
              }}
            />
          </div>

          {/* From Date */}
<div className="col-md-2">
  <input
    type="date"
    className="form-control form-control-sm"
    value={fromDate}
    onChange={(e) => {
      const value = e.target.value;

      setFromDate(value);

      if (toDate && toDate < value) {
        setToDate("");
      }

      setPageNumber(1);
    }}
  />
</div>

{/* To Date */}
<div className="col-md-2">
  <input
    type="date"
    className="form-control form-control-sm"
    value={toDate}
    min={fromDate || ""}
    onChange={(e) => {
      setToDate(e.target.value);
      setPageNumber(1);
    }}
  />
</div>
          <div className={isMember ? "col-md-6 d-flex justify-content-md-end align-items-center" : "col-md-4 d-flex justify-content-md-end align-items-center"}>


            <button
              className="btn btn-outline-primary btn-sm me-3"
              onClick={loadCardsBySheet}
            >
              <i className="fas fa-sync-alt"></i> Refresh
            </button>

            <span className="text-muted small me-3">
              Showing {start}-{end} of {totalRecords}
            </span>


            <nav>
              <ul className="pagination pagination-sm mb-0">

                <li className={`page-item ${pageNumber === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setPageNumber(prev => prev - 1)}
                  >
                    ‹
                  </button>
                </li>

                {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                  const page = startPage + i;

                  return (
                    <li
                      key={page}
                      className={`page-item ${page === pageNumber ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setPageNumber(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}

                <li className={`page-item ${pageNumber === totalPages ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setPageNumber(prev => prev + 1)}
                  >
                    ›
                  </button>
                </li>

              </ul>
            </nav>

          </div>
        </div>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
            <img src="/Images/loader.gif" alt="loading" style={{ width: "80px" }} />
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="kanban-board d-flex gap-3 overflow-auto">
              {boardData.columnOrder.map(columnId => {
                const column = boardData.columns[columnId];
                const tasks = column.taskIds.map(taskId => boardData.tasks[taskId]);
                return (
                  <div key={column.id} className="kanban-column">
                    <KanbanColumn
                      column={column}
                      tasks={tasks}
                      onCardClick={handleCardClick}
                      openModalSheet={openAddModal}

                    />
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        )}
        {selectedTask && (
          <>
            <div className="modal-backdrop fade show"></div>

            <div
              className="modal fade show"
              style={{ display: "block" }}
              tabIndex="-1"
              onClick={handleCloseModal}
            >
              <div
                className="modal-dialog modal-dialog-centered modal-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content border-0 shadow-lg">

                  {saving && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999
                      }}
                    >
                      <div className="spinner-border text-primary"></div>
                    </div>
                  )}

                  {/* HEADER */}
                  <div className="modal-header bg-light border-0 p-4">
                    <div className="d-flex align-items-center">
                      <span className="p-2 bg-primary-subtle text-primary-emphasis rounded-3 me-3">
                        <i className="fas fa-tasks fa-lg" />
                      </span>

                      <div>
                        <h5 className="modal-title mb-0 fw-bold">
                          {selectedTask.content} - {selectedTask.doctorName} - {selectedTask.vendor}
                        </h5>

                        <small className="text-muted">
                          in list:{" "}
                          <span className="fw-semibold">
                            {
                              boardData.columns[
                                Object.values(boardData.columns).find((col) =>
                                  col.taskIds.includes(selectedTask.id)
                                )?.id
                              ]?.title
                            }
                          </span>
                        </small>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn-close"
                      onClick={handleCloseModal}
                    ></button>
                  </div>

                  {/* BODY */}
                  <div className="modal-body p-4">

                    <div className="row mb-4">

                      {/* CLAIMANT */}
                      <div className="col-md-6">
                        <h6 className="fw-bold text-muted text-uppercase small mb-2">
                          Claimant / Case ID
                        </h6>

                        <textarea
                          className="form-control"
                          rows="3"
                          value={selectedTask.content}
                          readOnly
                        />
                      </div>

                      {/* DESCRIPTION */}
                      <div className="col-md-6">
                        <h6 className="fw-bold text-muted text-uppercase small mb-2">
                          Description
                        </h6>

                        <textarea
                          className="form-control"
                          rows="3"
                          value={selectedTask.description || ""}
                          onChange={(e) =>
                            setSelectedTask((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>

                    {/* IMAGE */}
                    {selectedTask.image && (
                      <div className="mb-4">
                        <h6 className="fw-bold text-muted text-uppercase small mb-2">
                          Attachment
                        </h6>
                        <img
                          src={selectedTask.image}
                          alt="Task attachment"
                          className="img-fluid rounded border"
                        />
                      </div>
                    )}

                    <div className="row g-4">

                      {/* LEFT */}
                      <div className="col-md-6">

                        {/* ASSIGNED USER */}
                        <div className="mb-3">
                          <h6 className="fw-semibold text-muted text-uppercase small mb-2">
                            Assigned To
                          </h6>

                          <div className="d-flex align-items-center">
                            <img
                              src="/Images/user.png"
                              height="20"
                              width="20"
                              alt="User avatar"
                              className="rounded-circle me-2"
                            />
                              {isMember ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={selectedTask?.assignedUser || ""}
                                  readOnly
                                />

                              ) : (
                              <Select
                                  isMulti
                                  closeMenuOnSelect={false}
                                  hideSelectedOptions={false}
                                   components={{
                                    Option,
                                    ClearIndicator: () => null,
                                    IndicatorSeparator: () => null,
                                    MultiValueRemove: () => null
                                  }}
                                styles={{
                                        container: (provided) => ({
                                          ...provided,
                                          width: "100%",
                                          flex: 1,
                                        }),

                                        option: (provided, state) => ({
                                          ...provided,
                                          backgroundColor: state.isSelected
                                            ? "#fff"           // selected hoy to white
                                            : state.isFocused
                                            ? "#f5f5f5"        // hover color
                                            : "#fff",

                                          color: "#333",

                                          ":active": {
                                            backgroundColor: "#f5f5f5",
                                          },
                                        }),
                                      }}
                                  options={users.map((u) => ({
                                    value: String(u.userId),
                                    label: u.userName
                                  }))}
                                  value={users
                                    .filter((u) =>
                                      selectedTask?.assignedUserIds ?.includes(String(u.userId))
                                    )
                                    .map((u) => ({
                                      value: String(u.userId),
                                      label: u.userName
                                    }))
                                  }
                                  onChange={(selectedOptions) => {
                                    setSelectedTask((prev) => ({
                                      ...prev,
                                      assignedUserIds:
                                        selectedOptions?.map((x) => x.value) || [],
                                      assignedUser:
                                        selectedOptions?.map((x) => x.label).join(", ") || ""
                                    }));
                                  }}
                                />
                                  )}
                          </div>
                        </div>

                        {/* DUE DATE */}
                        <div>
                          <h6 className="fw-semibold text-muted text-uppercase small mb-2">
                            Date
                          </h6>

                          <input
                            type="date"
                            className="form-control bg-light"
                            value={selectedTask?.dueDate ?? ""}
                            readOnly
                          />
                        </div>

                      </div>

                      {/* RIGHT */}
                      <div className="col-md-6">

                        {/* PRIORITY */}
                        <div className="mb-3">
                          <h6 className="fw-semibold text-muted text-uppercase small mb-2">
                            Priority
                          </h6>

                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter priority"
                            value={selectedTask?.prioritys ?? ""}
                            onChange={(e) =>
                              setSelectedTask((prev) => ({
                                ...prev,
                                prioritys: e.target.value,
                              }))
                            }
                          />
                        </div>

                        {/* TAGS */}
                        <div>
                          <h6 className="fw-semibold text-muted text-uppercase small mb-2">
                            Tags
                          </h6>

                          <div className="d-flex flex-wrap gap-2">

                            {selectedTask?.userType && (
                              <span className="badge bg-primary-subtle text-primary-emphasis px-3 py-1">
                                {selectedTask.userType}
                              </span>
                            )}

                            {Number(selectedTask?.prioritys) === 1 && (
                              <span className="badge bg-danger-subtle text-danger-emphasis px-3 py-1">
                                Rush Case
                              </span>
                            )}

                          </div>
                        </div>

                      </div>
                    </div>

                  </div>

                  {/* FOOTER */}
                  <div className="modal-footer bg-light border-0">

                    {!isMember && (
                      <>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSaveClick}
                        >
                          <i className="fas fa-save me-1"></i>
                          Save Changes
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => handleDeleteClick(selectedTask.id)}
                        >
                          <i className="fas fa-trash me-1"></i>
                          Delete
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseModal}
                    >
                      Close
                    </button>

                  </div>

                </div>
              </div>
            </div>
          </>
        )}
        {showDeleteConfirm && (
          <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
            <div className="modal fade show" style={{ display: 'block', zIndex: 1065 }} tabIndex="-1">
              <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
                <div className="modal-content border-0 shadow-lg">
                  <div className="modal-body p-4 text-center">
                    <div className="mb-3">
                      <i className="fas fa-exclamation-triangle fa-3x text-danger"></i>
                    </div>
                    <h5 className="fw-bold">Are you sure?</h5>
                    <p className="text-muted">Do you really want to delete this task? This process cannot be undone.</p>
                  </div>
                  <div className="modal-footer border-0 bg-light justify-content-center">
                    <button type="button" className="btn btn-secondary" onClick={handleCancelDelete}>Cancel</button>
                    <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>Yes, Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

    </div>
  );
}
